import { useCallback, useMemo, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ExamStatus, LicenseStatus, RegistrationApprovalStatus, RegistrationStatus } from "./type";
import moment from "moment";
import { selectOnboardingTrainingSummary } from "../../../../redux/selector";
import { color } from "../../../../../../mui-theme/palette";
import { mapLanguage } from "../../../../network/network";
import { useNavigate } from "react-router-dom";
import { ROOT_PATH, ONBOARDING_PATH } from "../../../../../../routes/constants";
import { useQuery } from "../../../../../../utils/hooks/use-query";
import { takeUIClickEvent } from "../../../../../../common/ga/ga";
import { TakeUIClickEventParameters } from "../../../../../../common/ga/types/ga-general-type";
import { GlobalHelper } from "../../../../../../utils/helpers/global-helper";
// PHKL Custom -s
import "moment/locale/zh-hk";
import { isPhone } from "../../../../../../constants/constants";

moment.updateLocale("zh-hk", {
  meridiem: function (hour, minute, isLower) {
    var hm = hour * 100 + minute;
    if (hm < 1200) {
      return "上午";
    } else {
      return "下午";
    }
  },
});
// PHKL Custom -e

interface HookProps {}
export const useLicenseExamEntranceCard = (props: HookProps) => {
  const { t, i18n } = useTranslation();
  const query = useQuery();
  const candidateId = query.get("candidateId") ?? GlobalHelper.getTokenUserSub();
  const navigate = useNavigate();
  const trainingAndExamSummary = useSelector(selectOnboardingTrainingSummary);
  const [isExpanded, setIsExpanded] = useState(false);
  const getCardColorStyle = useCallback(
    (status: RegistrationStatus | RegistrationApprovalStatus) => {
      if (status === RegistrationStatus.IN_PROGRESS) {
        return { statusBackgroundColor: color.cyanLight, statusTextColor: color.cyan };
      } else if (status === RegistrationStatus.COMPLETED) {
        return { statusBackgroundColor: color.greenLight, statusTextColor: color.green };
      } else if (status === RegistrationApprovalStatus.PASS) {
        return { statusBackgroundColor: color.greenLight, statusTextColor: color.green };
      } else if (status === RegistrationApprovalStatus.FAIL) {
        return { statusBackgroundColor: color.orangeLight, statusTextColor: color.orange };
      } else if (status === RegistrationApprovalStatus.PENDING_APPROVAL) {
        return { statusBackgroundColor: color.redLightest, statusTextColor: color.majorRed };
      } else if (status === RegistrationApprovalStatus.APPROVED) {
        return { statusBackgroundColor: color.cyanLight, statusTextColor: color.cyan };
      } else if (status === RegistrationApprovalStatus.REJECTED) {
        return { statusBackgroundColor: color.redLightest, statusTextColor: color.majorRed };
      } else if (status === RegistrationApprovalStatus.RETURNED) {
        return { statusBackgroundColor: color.yellowLight, statusTextColor: color.yellow };
        // PHKL Custom -s
      } else if (status === RegistrationApprovalStatus.PENDING) {
        return { statusBackgroundColor: color.purpleLight, statusTextColor: color.purple };
      } else if (status === RegistrationApprovalStatus.DQ) {
        return { statusBackgroundColor: "#F0E4C9", statusTextColor: "#948143" };
      }
      // PHKL Custom -e

      return { statusBackgroundColor: color.greyBg, statusTextColor: color.grey33 };
    },
    [],
  );
  const licenses = useMemo(() => {
    const licenses = getLicenseExamStatus((trainingAndExamSummary?.examSummary as any)?.data);
    if (!licenses) {
      return [];
    }

    //PHKL Custom -s
    const scheduleFormat = {
      date: "YYYY-MM-DD hh:mm",
      initDate: t("new_iiqe_registration_exam_schedule_start_date_format"),
      start: t("new_iiqe_registration_exam_schedule_start_time_format"), // PHKL Custom
      end: t("new_iiqe_registration_exam_schedule_end_time_format"),
    };
    const momentLang = getI18n().language === "zhhk" ? "zh-hk" : "en";

    const formatExamDate = (examDate: any, examStartTime: any, examEndTime: any) => {
      if (!examDate) {
        return {};
      }
      const date = moment(`${examDate} ${examStartTime}`, scheduleFormat.date).locale(momentLang);
      const startTime = date.format(scheduleFormat.start);
      const endTime = moment(`${examDate} ${examEndTime}`, scheduleFormat.date)
        .locale(momentLang)
        .format(scheduleFormat.end);
      const result = startTime.replace("{{}}", endTime).replaceAll("00分", "");
      return {
        date: `${date.format(scheduleFormat.initDate)} ${isPhone ? "" : result}`,
        time: isPhone ? result : undefined,
      };
    };

    return licenses.map((license: any) => {
      const { date, time } = formatExamDate(
        license?.examDate,
        license?.startTime,
        license?.endTime,
      );
      return {
        ...license,
        status: t(license.statusI18nKey),
        description: license.statusDesc,
        examName: license.examName,
        examType: license.examType,
        examSchedule: date, //PHKL Custom
        examScheduleTime: time, //PHKL Custom
        ...getCardColorStyle(license.status),
      };
    });
    //PHKL Custom -e
  }, [getCardColorStyle, t, trainingAndExamSummary?.examSummary]);

  const displayedLicenses = useMemo(() => {
    if (isExpanded || licenses.length <= 3) {
      return licenses;
    }
    return licenses.slice(0, 3);
  }, [licenses, isExpanded]);

  const showViewMore = useMemo(() => {
    return licenses.length > 3;
  }, [licenses]);

  const translations = useMemo(() => {
    return {
      title: t("Recruitment.license_exam_records"), //PHKL Custom
      more: t("Common.more"),
      viewMore: t("Common.show_more"),
      viewLess: t("Common.show_less"),
    };
  }, [t]);
  const handlePress = useCallback(
    (licenseOrExam: any) => {
      const gaData: TakeUIClickEventParameters = {
        module: "Recruitment",
        feature: "License",
        journey: "view_license_menu",
        stage: "journey_entry",
        screen_id: "SCR_RCRTMT_HM",
        screen_name: "Recruitment-Home-Screen",
        object_name: "License",
        object_type: "BUTTON",
      };
      takeUIClickEvent(gaData);

      if (!licenseOrExam.examCode) {
        navigate(
          `${ROOT_PATH}/${ONBOARDING_PATH}/exams?candidateId=${candidateId}&lang=${i18n.language}`,
        );
        return;
      }
      navigate(
        `${ROOT_PATH}/${ONBOARDING_PATH}/exams/registry/${licenseOrExam.examCode}?${
          licenseOrExam?.registrationId ? `&registrationId=${licenseOrExam?.registrationId}` : ""
        }${query.toString() ? `&${query.toString()}` : ""}`,
        {
          state: { data: licenseOrExam },
        },
      );
    },
    [navigate, query],
  );

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return {
    handlePress,
    translations,
    licenses,
    displayedLicenses,
    isExpanded,
    showViewMore,
    handleToggleExpand,
  };
};

// Function to determine the status and description
export function getLicenseExamStatus(data: any[]) {
  const startedExams = data?.filter?.(
    (exam) => exam.registrationStatus !== RegistrationStatus.NOT_STARTED,
  );
  if (!startedExams || startedExams.length === 0) {
    return [
      {
        statusI18nKey: "Common.not_enrolled", //PHKL Custom
        status: "NOT_STARTED",
        statusDesc: getI18n().t("Recruitment.complete_module_now"),
      },
    ];
  }
  const results = startedExams.map(mapLicenseExamStatus);
  // Sort by examDate and startTime in descending order (latest dates and times first)
  return results.sort((a, b) => {
    if (!a.examDate && !b.examDate) return 0;
    if (!a.examDate) return 1; // Items without examDate go to the end
    if (!b.examDate) return -1; // Items without examDate go to the end

    // Combine examDate and startTime into datetime strings
    const datetimeA = `${a.examDate} ${a.startTime || "00:00"}`;
    const datetimeB = `${b.examDate} ${b.startTime || "00:00"}`;

    return (
      moment(datetimeB, "YYYY-MM-DD HH:mm").valueOf() -
      moment(datetimeA, "YYYY-MM-DD HH:mm").valueOf()
    );
  });
}

function mapLicenseExamStatus(item: any) {
  if (item.examDate) {
    return mapExamRegistrationStatus(item);
  }
  const examName = item.examName?.[mapLanguage(getI18n().language)];
  const {
    registrationApprovalStates: registrationApprovalStatus,
    licenseApprovalStates: licenseStatus,
    registrationStatus,
  } = item;
  let examStatus = item.examStatus;

  if (registrationStatus === RegistrationStatus.TIMESLOT_CANCELLED) {
    examStatus = ExamStatus.CANCELLED;
  }

  if (
    registrationStatus === RegistrationStatus.NOT_STARTED &&
    (registrationApprovalStatus === RegistrationApprovalStatus.NOT_STARTED ||
      !registrationApprovalStatus) &&
    (examStatus === ExamStatus.NOT_STARTED || !examStatus) &&
    (licenseStatus === LicenseStatus.NOT_STARTED || !licenseStatus)
  ) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.not_enrolled", //PHKL Custom
      status: "NOT_STARTED",
      statusDesc: getI18n().t("Recruitment.complete_module_now"),
    };
  }

  if (licenseStatus === LicenseStatus.PENDING_APPROVAL) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.exam_proof_request_review"),
    };
  }

  if (licenseStatus === LicenseStatus.RETURNED) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.verification_returned_review_info"),
    };
  }

  if ([LicenseStatus.CANCELLED, LicenseStatus.REJECTED].includes(licenseStatus)) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.verification_cancelled_restart"),
    };
  }

  if (licenseStatus === LicenseStatus.APPROVED) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.COMPLETED",
      status: "COMPLETED",
      statusDesc: getI18n().t("Recruitment.exam_proof_request_passed_verification"),
    };
  }

  if (registrationApprovalStatus === RegistrationApprovalStatus.PENDING_APPROVAL) {
    const timeslot = `${moment(item.examDate, "YYYY-MM-DD").format("DD/MM/YYYY")} ${
      item.startTime
    }-${item.endTime}`;
    const statusDesc = getI18n().t("Recruitment.registered_timeslot", { timeslot });
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc,
    };
  }

  if (registrationApprovalStatus === RegistrationApprovalStatus.RETURNED) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.registration_returned_review_info"),
    };
  }

  if (examStatus === ExamStatus.CANCELLED) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.exam_cancelled_register_another_timeslot"),
    };
  }

  if (examStatus === ExamStatus.FAIL) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.exam_failed_register_again"),
    };
  }

  if (
    registrationApprovalStatus === RegistrationApprovalStatus.CANCELLED ||
    registrationApprovalStatus === RegistrationApprovalStatus.REJECTED
  ) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.IN_PROGRESS",
      status: "IN_PROGRESS",
      statusDesc: getI18n().t("Recruitment.registration_cancelled_restart"),
    };
  }

  if (
    [RegistrationApprovalStatus.APPROVED, RegistrationApprovalStatus.COMPLETED].includes(
      registrationApprovalStatus,
    ) &&
    (examStatus === ExamStatus.NOT_STARTED || !examStatus)
  ) {
    const timeslot = `${moment(item.examDate, "YYYY-MM-DD").format("DD/MM/YYYY")} ${
      item.startTime
    }-${item.endTime}`;
    const statusDesc = getI18n().t("Recruitment.registration_verified_timeslot", {
      timeslot,
    });
    return {
      ...item,
      examName,
      statusI18nKey: "Common.COMPLETED",
      status: "COMPLETED",
      statusDesc,
    };
  }

  if (examStatus === ExamStatus.PASS) {
    return {
      ...item,
      examName,
      statusI18nKey: "Common.COMPLETED",
      status: "COMPLETED",
      statusDesc: getI18n().t("Recruitment.exam_passed_congratulations"),
    };
  }

  return {
    ...item,
    examName,
    statusI18nKey: "Unknown",
    status: "Unknown",
    statusDesc: "Recruitment.status_info_not_available",
  };
}

// PHKL Custom -s
const getStatusI18nKey = (item: any) => {
  if (item.submittedByRole && item.registrationApprovalStates === "PENDING_APPROVAL") {
    switch (item.submittedByRole) {
      case "AGENT":
        return "Common.PENDING_APPROVAL_CANDIDATE";
      case "CANDIDATE":
        return "Common.PENDING_APPROVAL_AGENT";
    }
    return "Common.PENDING_APPROVAL";
  }

  return `Common.${item.registrationApprovalStates}`;
};
// PHKL Custom -e

const mapExamRegistrationStatus = (item: any) => {
  const examName = item.examName?.[mapLanguage(getI18n().language)];
  const { registrationApprovalStates, examCentre, examMode, examDate, score, startTime, endTime } =
    item;
  // exam schedule form looks like: 29/01/2025, Wed, 10:30 - 12:30
  // PHKL Custom -s
  // const examSchedule = `${moment(examDate, "YYYY-MM-DD").format("DD/MM/YYYY")}, ${moment(
  //   examDate,
  //   "YYYY-MM-DD",
  // ).format("ddd")}, ${startTime} - ${endTime}`;

  return {
    ...item,
    examName,
    statusI18nKey: getStatusI18nKey(item),
    status: registrationApprovalStates,
    // examSchedule, //PHKL Custom
    examCentre: examCentre,
    examMode,
    score,
  };
  // PHKL Custom -e
};
